import { useEffect, useState } from 'react';
import APIService, {
    IActiveContingentRisk,
    IActiveCountryAPI,
    IActiveDimensionAPI,
    IActiveGroupsAPIResponse,
    IAPIActiveClientFacingIndicator,
} from 'services/APIService';

type PreloadData = {
    dimensions: IActiveDimensionAPI;
    clientFacingIndicators: IAPIActiveClientFacingIndicator;
    contingentRisks: IActiveContingentRisk[];
    countries: IActiveCountryAPI;
    activeGroups: IActiveGroupsAPIResponse;
};

const fetchAll = async () => {
    const [
        dimensions,
        clientFacingIndicators,
        contingentRisks,
        countries,
        activeGroups
    ] = await Promise.all([
        APIService.getActiveDimensions(),
        APIService.getActiveClientFacingIndicators(),
        APIService.getActiveContingentRisks(),
        APIService.getActiveCountries(),
        APIService.getActiveGroups(),
    ]);
    return {
        dimensions,
        clientFacingIndicators,
        contingentRisks,
        countries,
        activeGroups
    };
};

const delay = retryCount => {
    return new Promise(resolve => setTimeout(resolve, 1000 * 2 ** retryCount));
};

const fetchRetry = async (retryCount = 1, lastError = null) => {
    if (retryCount > 5) throw new Error(lastError);
    try {
        return await fetchAll();
    } catch (e) {
        await delay(retryCount);
        return fetchRetry(retryCount + 1, e);
    }
};

const usePreload = (props: { hasGQPermissions: boolean }) => {
    const [error, setError] = useState<boolean>(false);
    const [preloadedData, setPreloadedData] = useState<PreloadData>();

    const preloadData = async () => {
        try {
            const data = await fetchRetry();
            setPreloadedData(data);
        } catch (e) {
            setError(true);
        }
    };
    useEffect(() => {
        if (!preloadedData && props.hasGQPermissions) {
            preloadData();
        }
    }, []);
    return { error, preloadedData };
};

export default usePreload;
